import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Mail, Search } from '@local/client-contracts';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { fade } from '@shared/animations/fade.animations';
import { EventInfo, EventInfoResource, EventsService } from '@shared/services';
import { windowSizeObserver } from '@shared/utils';
import { ComponentFocused } from '../../../../../models/component-focused.model';
import { PreviewFocus } from '../../../../../models/preview-focus';
import { HubService } from '../../../../../services/hub.service';
import { PreviewKeyboardService } from '../../../../../services/preview-keyboard.service';
import { MailBuildResultViewService, MailViewModel } from '../../../../results-views/mail/mail-build-result-view.service';
import { PreviewMode } from '../../../model/preview-mode';
import { MailService } from '../../services/mail.service';
@UntilDestroy()
@Component({
  selector: 'mail-preview',
  templateUrl: './mail-preview.component.html',
  styleUrls: ['./mail-preview.component.scss'],
  animations: [fade],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MailPreviewComponent implements OnInit {
  error: any;
  body: SafeHtml;
  startLoadBody: boolean;
  viewModel: MailViewModel;
  contentHeight: number;
  compIndex: number;
  childrenList: number[] = [];
  childComponentFocused: ComponentFocused = {};
  private content: Mail.Mail;
  private _model: Search.ResultResourceItem;
  private readonly marginContent: number = 26;
  private readonly marginContentHasAttachments: number = 56;
  private windowSize$ = windowSizeObserver();

  @Input() size: PreviewMode = 'side';
  @Input() isLauncher?: boolean = false;
  @Input() telemetrySearch: Partial<EventInfo>;
  @Input() set model(value: Search.ResultResourceItem) {
    if (!value) return;
    this.initData(value);
  }
  @ViewChild('previewContainer', { read: ElementRef }) previewContainerRef: ElementRef;
  @ViewChild('previewHeaderContainer', { read: ElementRef }) previewHeaderContainerRef: ElementRef;
  @ViewChild('previewAttachmentContainer', { read: ElementRef }) previewAttachmentContainerRef: ElementRef;
  @ViewChild('previewContent', { read: ElementRef }) previewContentRef: ElementRef<HTMLElement>;

  get model(): Search.ResultResourceItem {
    return this._model;
  }

  @Output() loaded = new EventEmitter<any>();

  private get resources(): Partial<EventInfoResource>[] {
    return [
      {
        id: this.model?.resource.id,
        list: this.hubService.currentLocation,
        appId: this.model.resource.appId,
        type: this.model.resource.type,
        linkId: this.model.resource.linkId,
      },
    ];
  }

  constructor(
    private mailService: MailService,
    private eventsService: EventsService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private mailBuildResultViewService: MailBuildResultViewService,
    private previewKeyboardService: PreviewKeyboardService,
    private hubService: HubService
  ) {}

  ngOnInit(): void {
    this.windowSize$.pipe(untilDestroyed(this)).subscribe(() => {
      this.setHeightContent();
      this.cdr.markForCheck();
    });
    this.previewKeyboardService.componentFocused$.pipe(untilDestroyed(this)).subscribe((val) => {
      if (!val || val.compId !== PreviewFocus.Preview) return;
      this.compIndex = val.direction === 'first' ? 0 : this.childrenList.length - 1;
      this.childComponentFocused = { compIndex: this.childrenList[this.compIndex], direction: val.direction };
      this.cdr.markForCheck();
    });
  }

  private initData(value: Search.ResultResourceItem) {
    if (!value) return;
    this._model = value;
    this.viewModel = this.mailBuildResultViewService.buildResultView(value, false, true);
    this.childrenList = this.viewModel.attachments?.length ? [1, 2] : [1];
    this.sendInitialEvent();
    this.load();
    this.loaded.emit();
  }

  async load() {
    this.startLoadBody = true;
    this.error = null;
    this.content = this.body = null;
    try {
      this.content = await this.mailService.get(this.model?.resource?.id);
      const body = this.content.body.replace(/\<a /g, "<a target='_blank' ");
      this.body = this.sanitizer.bypassSecurityTrustHtml(body);
    } catch (e) {
      this.error = e;
    } finally {
      this.startLoadBody = false;
      setTimeout(() => {
        if (this.previewContentRef) {
          let styleNodes: Node[] = [];
          const lastNodeIndex = this.previewContentRef.nativeElement.childNodes.length - 1;
          this.previewContentRef.nativeElement.childNodes.forEach((node, index) => {
            if (node.nodeName === 'STYLE' || (index === lastNodeIndex && node.nodeName === '#text')) {
              styleNodes.push(node);
            }
          });
          styleNodes = styleNodes.slice(0, styleNodes.length - 1);
          styleNodes?.forEach((node) => {
            this.previewContentRef.nativeElement.removeChild(node);
          });
        }
      }, 0);
    }
    this.setHeightContent();
    this.cdr.markForCheck();
  }

  private setHeightContent(): void {
    setTimeout(() => {
      this.contentHeight =
        this.previewContainerRef?.nativeElement['offsetHeight'] -
        this.previewHeaderContainerRef?.nativeElement['offsetHeight'] -
        this.previewAttachmentContainerRef?.nativeElement['offsetHeight'] -
        this.marginContent;
      this.contentHeight > 0
        ? (this.contentHeight =
            this.viewModel?.attachments?.length > 0 ? (this.contentHeight -= this.marginContentHasAttachments) : this.contentHeight)
        : this.contentHeight;
      this.cdr.markForCheck();
    }, 20);
  }

  onComponentFocusedExit(val: string) {
    if (val === 'prev') {
      if (--this.compIndex < 0) {
        this.previewKeyboardService.setComponentFocused('prev');
      }
      this.childComponentFocused = { compIndex: this.childrenList[this.compIndex], direction: 'last' };
    }

    if (val === 'next') {
      if (++this.compIndex >= this.childrenList.length) {
        this.previewKeyboardService.setComponentFocused('next');
      }
      this.childComponentFocused = { compIndex: this.childrenList[this.compIndex], direction: 'first' };
    }
    this.cdr.markForCheck();
  }

  private sendInitialEvent() {
    setTimeout(() => {
      this.eventsService.event('preview.resource', {
        label: this.size === 'popup' ? 'popup' : 'side_preview',
        resources: this.resources,
        location: {
          title: this.hubService.currentLocation,
        },
        ...this.telemetrySearch,
      });
    }, 0);
  }
}
